import React from 'react';
import { useForm, FormProvider } from 'react-hook-form';
import { joiResolver } from '@hookform/resolvers/joi';
import { showErrorToast } from '@shared/services/toastService';
import { ObservationReportValidator } from '../validators/ObservationReportValidator';
import { ObservationReportInputFields } from './ObservationReportInputFields';
import { ObservationReportDisplay } from './ObservationReportDisplay';

const REQUIRED_FIELDS_ERROR_MESSAGE = 'Observation Report must be added prior to submission.';

/**
 * @typedef {object} ObservationReportUploadObservationReportDocument
 * @property {string} [fileName]
 */

/**
 * @typedef {object} ObservationReportUploadObservationReport
 * @property {string} [observedUserName]
 * @property {ObservationReportUploadObservationReportDocument} [document]
 */

/**
 * @typedef {object} ObservationReportUploadProps
 * @property {ObservationReportUploadObservationReport} [observationReport]
 * @property {number} [eventId]
 * @property {number} [observedUserId]
 * @property {boolean} [dataLoading]
 * @property {boolean} [uploadFileLoading]
 * @property {(report: { eventId: number, observedUserId: number, file: File | null }) => void | Promise<void>} [handleAddObservationReport]
 * @property {() => void} [handleDownloadObservationReportFile]
 */

/**
 * @param {ObservationReportUploadProps} props
 */
export const ObservationReportUpload = props => {
  const {
    observationReport,
    eventId,
    observedUserId,
    dataLoading,
    handleAddObservationReport,
    handleDownloadObservationReportFile,
    uploadFileLoading
  } = props;

  const form = useForm({
    resolver: joiResolver(ObservationReportValidator.addSchema),
    defaultValues: {
      file: null
    }
  });

  const { handleSubmit } = form;

  const onSubmit = async formData => {
    await handleAddObservationReport({
      eventId,
      observedUserId,
      ...formData
    });
  };

  const onError = () => {
    showErrorToast(REQUIRED_FIELDS_ERROR_MESSAGE);
  };

  return (
    <>
      {observationReport?.document?.fileName ? (
        <ObservationReportDisplay
          loading={dataLoading}
          observationReport={observationReport}
          onDownload={handleDownloadObservationReportFile}
        />
      ) : (
        <FormProvider {...form}>
          <form
            id='observation-report-form'
            className='add-observation-form'
            data-testid='add-observation-form'
            onSubmit={handleSubmit(onSubmit, onError)}>
            <ObservationReportInputFields
              loading={dataLoading}
              hideRemoveButton={uploadFileLoading}
              observedUserName={observationReport?.observedUserName}
            />
          </form>
        </FormProvider>
      )}
    </>
  );
};
